<script>
import TrucksMixin from "@/views/trucks/TrucksMixin.vue";
import OmniDialog from "@/components/OmniDialog.vue";
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store";
import { useDateTime } from "@/composables/index.ts";

const { dateTZ } = useDateTime();

export default {
  name: "ReserveTruckAction",
  components: { OmniDialog },
  mixins: [TrucksMixin],
  props: {
    truck: Object,
    disabled: Boolean,
  },
  emits: ["success", "error"],
  data() {
    return {
      dialogVisible: false,
      dispatcherId: null,
      time: 15,
      timeInMinutes: [5, 10, 15, 20, 30, 45, 60],
    };
  },
  computed: {
    ...mapState(useAppStore, ["dispatcher", "dispatchers"]),
    reservedByMe() {
      return this.isReservedByDispatcher(this.truck, this.dispatcher.id) && !this.reservationExpired;
    },
    reservationExpired() {
      return this.getReservationTime(this.truck) < 0;
    },
    minutes() {
      return this.timeInMinutes.map((t) => {
        return {
          id: t,
          name: t + " minutes",
        };
      });
    },
    sortedDispatchers() {
      return this.dispatchers.filter((d) => d.id !== this.dispatcher.id);
    },
    reservedTill() {
      return dateTZ(this.truck.reservedTill, this.dispatcher.timezone);
    },
    title() {
      if (!this.reservedByMe || !this.truck.reservedById || this.reservationExpired) {
        return `Reserve truck #${this.truck.number}`;
      }

      return `Truck #${this.truck.number} reservation management`;
    },
  },
  methods: {
    showDialog() {
      if (!this.disabled) {
        this.dialogVisible = true;
      }
    },
    closeDialog() {
      this.dialogVisible = false;
    },
    async save() {
      if (this.truck.reservedById === null || this.reservedByMe || this.reservationExpired) {
        await this.reserveTruck(this.dispatcherId ? this.dispatcherId : this.dispatcher.id, this.time);
      } else {
        this.closeDialog();
      }
    },
    async releaseTruck() {
      if (this.reservedByMe) {
        await this.reserveTruck(null);
      } else {
        this.closeDialog();
      }
    },
    async reserveTruck(dispatcherId, minutes = null) {
      try {
        const resp = await this.$api.trucks.reserveTruck(this.truck.id, dispatcherId, minutes);
        if (resp.success) {
          this.$emit("success", resp.data);
        }
      } catch (e) {
        console.error("Error reserving truck: " + e);
        this.$emit("error", e);
      } finally {
        this.closeDialog();
      }
    },
  },
};
</script>

<template>
  <div @click="showDialog">
    <slot></slot>
  </div>

  <omni-dialog
    v-model="dialogVisible"
    :primary-action-label="reservedByMe ? 'Save' : 'Reserve'"
    :secondary-action-label="reservedByMe ? 'Release truck' : null"
    :title="title"
    size="small"
    width="550"
    @primary-action:click="save"
    @secondary-action:click="releaseTruck"
  >
    <span class="text-grey-darken-3">{{
      truck.reservedById && !reservationExpired
        ? "Reserved by " + getReservedBy(truck) + " until " + reservedTill
        : null
    }}</span>
    <div class="my-5">
      <div v-if="truck.reservedById && !reservationExpired" class="mb-4"></div>

      <v-select
        v-if="reservedByMe"
        v-model="dispatcherId"
        :items="sortedDispatchers"
        class="mb-7"
        color="primary"
        density="compact"
        hide-details
        item-title="name"
        item-value="id"
        label="Transfer reservation to"
        variant="outlined"
      />

      <v-select
        v-model="time"
        :items="minutes"
        :label="reservedByMe ? 'Extend reservation for' : 'Reserve for'"
        color="primary"
        density="compact"
        hide-details
        item-title="name"
        item-value="id"
        variant="outlined"
      />
    </div>
  </omni-dialog>
</template>
